import React, { useCallback, useEffect, useReducer, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Paper, Typography, Divider, Button } from "@mui/material";
import Loader from "../components/common/custom-component/Loader";
import TabsManager from "../components/common/custom-component/TabsManager";
import AccordionManager from "../components/common/custom-component/AccordionManager";
import InfoStackPanel from "../components/common/custom-component/InfoStackPanel";
import { useDispatch, useSelector } from "react-redux";
import { fetchServicesDataThunk } from "../store/thunks/mdsThunks";
import {
  initialState,
  manageDynamicServiceReducer,
} from "../reducers/manageDynamicServiceReducer";
import { useTranslation } from "react-i18next";
import useAuth from "hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { claimTaskThunk, startWorkflowProcessThunk } from "store/thunks/workflowThunks";
import { logger } from "helpers/logger";
import { getErrorMessage } from "helpers/errorUtils";
import { downloadFromUrl } from "helpers/downloadUtils";

const DynamicServicePage = () => {
  const keycloakAuth = useAuth();
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { serviceName } = useParams();
  const dispatch = useDispatch();
  const domainId = useSelector((state) => state?.domainConfig?.data?.domain_id);
  const workflowListKeys = useSelector((state) => state?.userAccessData?.data?.data?.userWorkflowListDefinitionKeys);

  const [state, dispatchState] = useReducer(
    manageDynamicServiceReducer,
    initialState
  );

  const {
    data: {
      serviceRow,
      tabsData,
      faqData,
      serviceDetailsData,
      buttonsData,
      pageTitle,
      pageSubtitle,
    },
    loading: { isLoadingPage },
    error: { hasError, message },
  } = state;

  // #region HELPERS
  const resolveUrlFrom = useCallback((cfg, urlFrom) => {
    // supports "meta.manual_url"
    if (!urlFrom || typeof urlFrom !== "string") return null;
    if (urlFrom === "meta.manual_url") return cfg?.meta?.manual_url || null;
    return null;
  }, []);
  // #endregion

  // #region CONFIG
  const parsedConfig = useMemo(() => {
    let cfg = serviceRow?.config ?? null;
    if (typeof cfg === "string") {
      try {
        cfg = JSON.parse(cfg);
      } catch {
        cfg = null;
      }
    }
    return cfg && typeof cfg === "object" ? cfg : {};
  }, [serviceRow]);

  const configMeta = parsedConfig?.meta || {};
  // #endregion

  // #region BUTTON HANDLERS
  const handleStartService = useCallback(
    async (btn) => {
      if (!domainId || !serviceName) return;

      if (!keycloakAuth.isAuthenticated) {
        navigate("/login"); // Redirect to login page
        window.location.reload();
      }

      const workflowId = btn?.workflowId;

      if (workflowListKeys?.includes(workflowId)) {
        const startProcessResponse = await dispatch(
          startWorkflowProcessThunk({
            definitionKey: workflowId,
            assignee: keycloakAuth?.user?.id
          })
        );
        if (startWorkflowProcessThunk.fulfilled.match(startProcessResponse)) {
          let formData = startProcessResponse?.payload?.data?.formData;
          const taskID = startProcessResponse?.payload?.data?.getCurrentUserTaskResponse?.getFormByTaskResponse?.taskId;

          if (keycloakAuth.token) await dispatch(claimTaskThunk({ taskId: taskID, userId: keycloakAuth?.user?.id }));

          navigate("/formRenderer", { state: { data: formData } });
        } else {
          logger.error("Failed to start process");
        }
      }

    },
    [domainId, serviceName, keycloakAuth.isAuthenticated, navigate]
  );

  const handleDownloadManual = useCallback(
    (btn) => {
      const manualURL =
        resolveUrlFrom(parsedConfig, btn?.url_from) ||
        configMeta?.manual_url ||
        serviceRow?.manual_url; // fallback only

      downloadFromUrl(manualURL);
    },
    [parsedConfig, resolveUrlFrom, configMeta, serviceRow]
  );

  const handlerMap = useMemo(
    () => ({
      handleStartService,
      handleDownloadManual,
    }),
    [handleStartService, handleDownloadManual]
  );

  // Button decides its handler from DB: btn.handler
  const handleButtonClick = useCallback(
    (btn) => {
      const handlerName = btn?.handler;
      const fn = handlerName ? handlerMap?.[handlerName] : null;

      if (typeof fn === "function") {
        fn(btn);
      }
    },
    [handlerMap]
  );
  // #endregion

  // #region FETCH_DATA
  const loadPageData = useCallback(async () => {
    if (!domainId || !serviceName) return;

    dispatchState({
      type: "SET_LOADING",
      payload: { key: "isLoadingPage", value: true },
    });
    dispatchState({ type: "CLEAR_ERROR" });

    try {
      const result = await dispatch(
        fetchServicesDataThunk({
          domainId,
          pageName: serviceName,
        })
      ).unwrap();

      const rows = Array.isArray(result?.data) ? result?.data : [];
      const row = rows?.[0] || null;

      dispatchState({ type: "SET_SERVICE_ROW", payload: row });

      if (!row) {
        dispatchState({
          type: "SET_ERROR",
          payload: "No page data found for this service.",
        });
        return;
      }

      // config: { meta, buttons, tabs_data, faq_data, service_details_data }
      let config = row?.config ?? {};
      if (typeof config === "string") {
        try {
          config = JSON.parse(config);
        } catch {
          config = {};
        }
      }

      const meta = config?.meta || {};

      // Title / Subtitle
      dispatchState({
        type: "SET_PAGE_META",
        payload: {
          pageTitle: meta?.title ?? row?.title ?? "",
          pageSubtitle: meta?.subtitle ?? row?.subtitle ?? "",
        },
      });

      // tabs_data
      let tabs = config?.tabs_data ?? [];
      if (typeof tabs === "string") {
        try {
          tabs = JSON.parse(tabs);
        } catch {
          tabs = [];
        }
      }
      dispatchState({
        type: "SET_TABS_DATA",
        payload: Array.isArray(tabs) ? tabs : [],
      });

      // faq_data
      let faq = config?.faq_data ?? [];
      if (typeof faq === "string") {
        try {
          faq = JSON.parse(faq);
        } catch {
          faq = [];
        }
      }
      dispatchState({
        type: "SET_FAQ_DATA",
        payload: Array.isArray(faq) ? faq : [],
      });

      // service_details_data
      let details = config?.service_details_data ?? [];
      if (typeof details === "string") {
        try {
          details = JSON.parse(details);
        } catch {
          details = [];
        }
      }
      dispatchState({
        type: "SET_SERVICE_DETAILS_DATA",
        payload: Array.isArray(details) ? details : [],
      });

      // buttons (array OR object)
      let buttons = config?.buttons ?? [];
      if (typeof buttons === "string") {
        try {
          buttons = JSON.parse(buttons);
        } catch {
          buttons = [];
        }
      }
      if (buttons && typeof buttons === "object" && !Array.isArray(buttons)) {
        buttons = Object.values(buttons).filter(Boolean);
      }
      dispatchState({
        type: "SET_BUTTONS_DATA",
        payload: Array.isArray(buttons) ? buttons : [],
      });
    } catch (err) {
      dispatchState({
        type: "SET_ERROR",
        payload: getErrorMessage(err, "Failed to load service page data"),
      });
    } finally {
      dispatchState({
        type: "SET_LOADING",
        payload: { key: "isLoadingPage", value: false },
      });
    }
  }, [dispatch, domainId, serviceName]);
  // #endregion

  // #region EFFECTS
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);
  // #endregion

  // #region RENDER
  const hasTabs = Array.isArray(tabsData) && tabsData.length > 0;
  const hasFaq = Array.isArray(faqData) && faqData.length > 0;
  const hasDetails =
    Array.isArray(serviceDetailsData) && serviceDetailsData.length > 0;
  const hasButtons = Array.isArray(buttonsData) && buttonsData.length > 0;

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Box
        sx={{
          // 10% margin on right + left
          maxWidth: "80%",
          mx: "auto",
          px: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ p: { xs: 1, md: 1 } }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {t(pageTitle)}
            </Typography>

            {pageSubtitle ? (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                {t(pageSubtitle)}
              </Typography>
            ) : null}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {isLoadingPage ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <Loader />
            </Box>
          ) : hasError ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="h6" color="error">
                {message}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Row 1 */}
              {(hasTabs || hasDetails || hasButtons) && (
                <Box
                  sx={{
                    display: { xs: "block", md: "flex" },
                    gap: 2,
                    alignItems: "stretch",
                  }}
                >
                  {/* Steps / Tabs (70%) */}
                  {hasTabs && (
                    <Paper
                      elevation={2}
                      sx={{
                        flex: { md: "0 0 70%" },
                        width: { xs: "100%", md: "70%" },
                        borderRadius: 2,
                        p: { xs: 2, md: 2.5 },
                      }}
                    >
                      <TabsManager tabs={tabsData} />
                    </Paper>
                  )}

                  {/* Details (30%) */}
                  {(hasDetails || hasButtons) && (
                    <Paper
                      elevation={2}
                      sx={{
                        flex: { md: "0 0 30%" },
                        width: { xs: "100%", md: "30%" },
                        borderRadius: 2,
                        p: { xs: 2, md: 2.5 },
                      }}
                    >
                      {hasButtons && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.25,
                            mb: hasDetails ? 1.75 : 0,
                          }}
                        >
                          {buttonsData.map((btn) => {
                            const isPrimary = btn?.style === "primary";
                            const isDownload = !!btn?.isDownload;

                            const manualURL = isDownload
                              ? resolveUrlFrom(parsedConfig, btn?.url_from) ||
                              configMeta?.manual_url
                              : null;

                            return (
                              <Button
                                key={btn?.id || btn?.type}
                                fullWidth
                                variant={isPrimary ? "contained" : "outlined"}
                                sx={{
                                  borderRadius: 999,
                                  textTransform: "none",
                                  fontWeight: isPrimary ? 700 : 600,
                                  bgcolor: isPrimary ? "#176342" : undefined,
                                  boxShadow: "none",
                                  color: !isPrimary ? "#176342" : undefined,
                                  borderColor: !isPrimary ? "#176342" : undefined,
                                  "&:hover": isPrimary
                                    ? { bgcolor: "#176342", boxShadow: "none" }
                                    : {
                                      borderColor: "#176342",
                                      bgcolor: "rgba(31, 122, 84, 0.06)",
                                    },
                                }}
                                onClick={() => handleButtonClick(btn)}
                                disabled={isDownload && !manualURL}
                              >
                                {t(btn?.label || "")}
                              </Button>
                            );
                          })}
                        </Box>
                      )}

                      {hasDetails && (
                        <InfoStackPanel
                          title="Service details"
                          subtitle="Find out more details"
                          items={serviceDetailsData}
                          containerSx={{
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "none",
                          }}
                          headerSx={{ mb: 1.5 }}
                          itemSx={{ bgcolor: "#f7f7f7" }}
                        />
                      )}
                    </Paper>
                  )}
                </Box>
              )}

              {/* Row 2 (FAQ 100%) */}
              {hasFaq && (
                <Paper
                  elevation={2}
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    p: { xs: 2, md: 2.5 },
                  }}
                >
                  <AccordionManager items={faqData} title="FAQ" />
                </Paper>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
  // #endregion
};

export default DynamicServicePage;
